import React, {createRef, ReactNode, RefObject} from "react";
import Message from "./log/Message";
import {initMessageGlobals, MessageInterface} from "./log/MessageInterface";
import portalConfiguration from "./configuration/PortalConfiguration";
import MessageBox from "./components/MessageBox";
import i18next from "i18next";
import Immutable from "./immutable/Immutable";
import {initHelperGlobals} from "./components/Helper";
import Translate from "./translation/Translate";
import FunctionHelper from "./helper/FunctionHelper";
import {AbstractPageState} from "./AbstractPageInterface";
import {initMessageLogGlobals} from "./log/MessageLogInterface";
import PortalComponent from "./components/PortalComponent";

/**
 * An AbstractPage component is used to represent a new webpage
 */
export default abstract class AbstractPage<P, S extends AbstractPageState> extends PortalComponent<P, S> {
	protected pageElement: RefObject<HTMLDivElement>;

	/**
	 * Constructs an {@link AbstractPage} component
	 */
	constructor(props: Readonly<any>) {
		super(props);

		this.pageElement = createRef<HTMLDivElement>();

		this.addMessage = this.addMessage.bind(this);
		this.setState = this.setState.bind(this);

		this.state = {
			...initMessageLogGlobals(),
			...initHelperGlobals(),
			...initMessageGlobals()
		} as S;
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		// prevent browser zoom
		if (this.pageElement.current !== null) {
			this.pageElement.current.addEventListener('wheel', (event: WheelEvent) => {
				if (event.ctrlKey) {
					event.preventDefault();
				}
			});
		}
	}

	/**
	 * Adds a {@link Message} to the pages {@link AbstractPageState.messages}. It will automatically be rendered after adding.
	 */
	public addMessage(message: Message) {
		let messages: Array<MessageInterface> = this.state.messages.get();
		messages.push(message);

		if (messages.length > portalConfiguration.infoBoxMaximum) {
			messages.shift();
		}

		this.setState({
			messages: Immutable.from(messages)
		});
	}

	/**
	 * Render the {@link Message}s defined in the {@link AbstractPageState.messages} as {@link MessageBox}es
	 */
	protected renderMessages(): Array<ReactNode> {
		let renderedMessages: Array<ReactNode> = [];
		let messages: Array<MessageInterface> = this.state.messages.get();

		for (let i: number = messages.length - 1; i >= messages.length - portalConfiguration.infoBoxMaximum && i >= 0; i--) {
			if (!FunctionHelper.exists(messages[i])) {
				continue;
			}

			let message: Message | undefined = Message.fromJson(messages[i]);

			renderedMessages.push(
				<MessageBox type={message.type} closeable={true} fade={true} timeout={portalConfiguration.infoBoxDelay}
							key={message.id} visible={message.visible} language={i18next.language}>
					{Translate.value(message.message, {ns: "message", ...message.context})}
				</MessageBox>
			);
		}

		return renderedMessages;
	}
}